import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Clock, Shield, MapPin, Award, Users, Target, ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";

const values = [
  {
    icon: Clock,
    title: "Rapidité",
    description: "Intervention sous 24-48h dans toute la région. Votre temps est précieux, nous le respectons.",
  },
  {
    icon: Shield,
    title: "Qualité",
    description: "Vitrages certifiés OEM et garantie à vie. Nous ne faisons aucun compromis sur la qualité.",
  },
  {
    icon: MapPin,
    title: "À domicile",
    description: "Nous venons à vous, que ce soit chez vous ou sur votre lieu de travail. Plus besoin de vous déplacer.",
  },
  {
    icon: Award,
    title: "Expertise ADAS",
    description: "Techniciens certifiés pour le calibrage des systèmes d'aide à la conduite les plus récents.",
  },
];

const stats = [
  { value: "500+", label: "Interventions par an" },
  { value: "98%", label: "Clients satisfaits" },
  { value: "24h", label: "Délai d'intervention" },
  { value: "5 ans", label: "D'expérience" },
];

export default function AboutPage() {
  return (
    <div className="pt-20 sm:pt-24 lg:pt-28" data-testid="about-page">
      {/* Hero */}
      <section className="section-padding bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-sky-500/20 text-sky-400 rounded-full text-sm font-medium mb-6">
                À propos de nous
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                MY VITREO, votre partenaire vitrage en Lorraine
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                Depuis notre création, nous nous engageons à fournir un service d'excellence 
                pour tous vos besoins en vitrage automobile. Notre mission : vous offrir 
                une solution rapide, professionnelle et de qualité, directement à domicile.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="glass-card p-4 rounded-3xl bg-white/5 border-white/10">
                <img
                  src="https://customer-assets.emergentagent.com/job_my-vitreo-preview/artifacts/98ps50mz_IMG_8930.jpeg"
                  alt="Équipe MY VITREO"
                  className="w-full h-[400px] object-cover rounded-2xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-sky-500">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <p className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.value}
                </p>
                <p className="text-sky-100">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sky-400 font-medium uppercase tracking-wider text-sm">
              Nos valeurs
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3">
              Ce qui nous distingue
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-8 rounded-3xl text-center card-hover"
              >
                <div className="w-16 h-16 bg-sky-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-sky-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-padding bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <Target className="w-8 h-8 text-sky-400" />
                <span className="text-sky-400 font-medium uppercase tracking-wider text-sm">
                  Notre mission
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Rendre le remplacement de vitrage simple et accessible
              </h2>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                Chez MY VITREO, nous croyons que le remplacement de pare-brise ne devrait 
                pas être une source de stress. C'est pourquoi nous avons développé un 
                service complet qui simplifie chaque étape du processus.
              </p>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                De la prise de rendez-vous en ligne à l'intervention à domicile, 
                en passant par la prise en charge administrative avec votre assurance, 
                nous nous occupons de tout pour que vous n'ayez à vous soucier de rien.
              </p>

              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-sky-400" />
                <p className="text-slate-200 font-medium">
                  Une équipe de techniciens passionnés à votre service
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 rounded-3xl"
            >
              <h3 className="text-2xl font-bold text-white mb-6">
                Zone d'intervention
              </h3>
              <p className="text-slate-300 mb-6">
                Basé à Metz, nous intervenons dans toute la région Lorraine et le Grand Est :
              </p>
              <div className="space-y-3">
                {["Moselle (57)", "Meurthe-et-Moselle (54)", "Vosges (88)", "Meuse (55)"].map((dept) => (
                  <div key={dept} className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-xl">
                    <MapPin className="w-5 h-5 text-sky-400" />
                    <span className="text-slate-200 font-medium">{dept}</span>
                  </div>
                ))}
              </div>
              <p className="text-slate-500 text-sm mt-6">
                Et plus largement dans tout le Grand Est sur demande.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-sky-500">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Prêt à découvrir notre service ?
            </h2>
            <p className="text-sky-100 text-lg mb-8">
              Contactez-nous pour un devis gratuit ou prenez rendez-vous directement en ligne.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/rendez-vous">
                <Button className="w-full sm:w-auto bg-white text-sky-400 hover:bg-sky-500/20 rounded-full px-8 py-6 text-lg font-semibold">
                  Prendre rendez-vous
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/devis">
                <Button variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold">
                  Demander un devis
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
